import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Crosshair, Info, Search, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { fmt, fmtPrecise, formatSpeed } from '../format'
import { clipColor, clipLabel, MIN_CLIP_S, type ProjectClip } from '../model'
import { PAN_STEP, useTimelineView } from '../hooks/useTimelineView'
import { TagLanes, type Tag } from './TagLanes'
import styles from '../styles/editor.module.scss'

/** Arming state of the "new clip" flow. */
export type Armed = { phase: 'start' } | { phase: 'end'; start: number }

/**
 * Active drag on the timeline.
 *
 * The origin snapshot lets every mousemove recompute the target from
 * absolute mouse-X instead of accumulating deltas, so sub-pixel losses
 * never drift the clip.
 */
type DragState =
  | {
      kind: 'start' | 'end' | 'range'
      clipId: number
      originX: number
      originalStart: number
      originalEnd: number
    }
  | { kind: 'playhead'; originX: number; originalPlayhead: number; resumeAfter: boolean }

/** Ignore drags under this many pixels — they're clicks with a shaky hand. */
const DRAG_SLOP_PX = 2
/** How far a trackpad has to swipe sideways to pan a full window. */
const PAN_PIXELS_PER_SPAN = 400
/** Arrow-key nudge on a focused marker/clip; Shift takes the coarse step.
 * Finer than any mouse drag, which is the point — this is how you land on
 * an exact frame. */
const NUDGE_FINE_S = 0.1
const NUDGE_COARSE_S = 1

/** A clip placed on the project timebase. ``start``/``end`` are project
 * seconds — the clip's own bounds are in its source file's clock, and the
 * two only coincide when the project has a single video. */
export interface PlacedClip {
  clip: ProjectClip
  /** Position in the project's clip list, for the badge number. */
  index: number
  start: number
  end: number
  /**
   * Held stretches inside this bar, on the timebase.
   *
   * They are drawn *inside* the clip and in its own colour, hatched and
   * darker — the decision was that a freeze lengthens the clip it belongs
   * to rather than becoming a block of its own, so a glance tells you which
   * clip a frozen stretch came from. Empty in "vídeos completos", where a
   * clip bar marks source footage and a freeze consumes none.
   */
  freezes: { id: string; start: number; end: number }[]
}

export interface TimelineProps {
  duration: number
  playhead: number
  /** Every clip in the project, mapped onto the timebase. */
  clips: PlacedClip[]
  /** Project seconds where one source video gives way to the next. */
  joins: number[]
  /**
   * Whether bounds can be dragged. Off in "só os cortes": there the
   * timeline *is* the clip list, so resizing a clip reflows everything
   * after it — the bar would slide out from under the cursor mid-drag.
   */
  editable: boolean
  armed: Armed | null
  /** Click on empty timeline: seek, or place the armed marker. */
  onPickTime: (time: number) => void
  /** Playhead moved by a drag — optimistic UI + video seek. */
  onScrub: (time: number) => void
  /** A clip's bounds changed (fires throughout the drag). */
  onClipBounds: (clipId: number, start: number, end: number) => void
  /** Grabbing a marker pauses playback; this asks for it back on release. */
  onResumePlayback: () => void
  isPlaying: boolean
  onPausePlayback: () => void
  /** Time-ranged effects (captions today, speed ramps next), drawn under
   * the timeline on the same axis and the same zoom window. */
  tags: Tag[]
  selectedTagId: string | null
  onSelectTag: (id: string | null) => void
  onTagChange: (id: string, start: number, end: number) => void
  onDuplicateTag?: ((id: string) => void) | undefined
  onDeleteTag?: ((id: string) => void) | undefined
}

export function Timeline({
  duration,
  playhead,
  clips,
  joins,
  editable,
  armed,
  onPickTime,
  onScrub,
  onClipBounds,
  onResumePlayback,
  isPlaying,
  onPausePlayback,
  tags,
  selectedTagId,
  onSelectTag,
  onTagChange,
  onDuplicateTag,
  onDeleteTag,
}: TimelineProps) {
  const timelineRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  // Set on the first significant move of a drag; consulted by the click
  // handler so releasing a drag doesn't also seek or advance the arming.
  const dragMovedRef = useRef(false)
  const { view, zoomed, scale, zoomAt, panBy, centerOn, reset, timeAt, percentOf, secondsPerPixel } =
    useTimelineView(duration)

  const viewRef = useRef(view)
  viewRef.current = view
  const draggingRef = useRef(false)
  draggingRef.current = drag !== null

  // --- Zoom / pan (native listener: React's onWheel is passive, so it
  // can't preventDefault the page scroll) ----------------------------------
  useEffect(() => {
    const el = timelineRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (duration <= 0) return
      e.preventDefault()
      // Sideways scroll (trackpad) or shift+wheel pans; everything else
      // zooms — including pinch, which browsers deliver as ctrl+wheel.
      const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY)
      if (e.shiftKey) {
        panBy(e.deltaY > 0 ? PAN_STEP : -PAN_STEP)
        return
      }
      if (horizontal) {
        panBy(e.deltaX / PAN_PIXELS_PER_SPAN)
        return
      }
      const rect = el.getBoundingClientRect()
      zoomAt((e.clientX - rect.left) / rect.width, e.deltaY)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      el.removeEventListener('wheel', onWheel)
    }
  }, [duration, zoomAt, panBy])

  // Keep the playhead in frame while zoomed — otherwise playing a video
  // at 20× zoom shows a still window the cursor left long ago.
  useEffect(() => {
    if (!zoomed || draggingRef.current) return
    const { start, span } = viewRef.current
    if (playhead < start || playhead > start + span) centerOn(playhead, 0.35)
  }, [playhead, zoomed, centerOn])

  // --- Drags ---------------------------------------------------------------
  const startClipDrag = (e: React.MouseEvent, kind: 'start' | 'end' | 'range', placed: PlacedClip) => {
    if (e.button !== 0 || !editable) return
    e.stopPropagation()
    e.preventDefault()
    setDrag({
      kind,
      clipId: placed.clip.id,
      originX: e.clientX,
      originalStart: placed.start,
      originalEnd: placed.end,
    })
    dragMovedRef.current = false
  }

  const startPlayheadDrag = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    e.stopPropagation()
    e.preventDefault()
    // Pause while scrubbing: otherwise the element's own timeupdate
    // fights our seeks and the playhead ping-pongs.
    const wasPlaying = isPlaying
    if (wasPlaying) onPausePlayback()
    setDrag({
      kind: 'playhead',
      originX: e.clientX,
      originalPlayhead: playhead,
      resumeAfter: wasPlaying,
    })
    dragMovedRef.current = false
  }

  useEffect(() => {
    if (!drag) return
    const el = timelineRef.current
    if (!el || duration <= 0) return
    const width = el.getBoundingClientRect().width
    const perPixel = secondsPerPixel(width)

    const onMove = (e: MouseEvent) => {
      const deltaPx = e.clientX - drag.originX
      if (Math.abs(deltaPx) > DRAG_SLOP_PX) dragMovedRef.current = true
      const deltaSec = deltaPx * perPixel

      if (drag.kind === 'playhead') {
        onScrub(Math.max(0, Math.min(duration, drag.originalPlayhead + deltaSec)))
        return
      }
      if (drag.kind === 'start') {
        const start = Math.max(
          0,
          Math.min(drag.originalEnd - MIN_CLIP_S, drag.originalStart + deltaSec),
        )
        onClipBounds(drag.clipId, start, drag.originalEnd)
        // The point of moving a start marker is to find the right frame —
        // so the player follows it.
        onScrub(start)
        return
      }
      if (drag.kind === 'end') {
        const end = Math.max(
          drag.originalStart + MIN_CLIP_S,
          Math.min(duration, drag.originalEnd + deltaSec),
        )
        onClipBounds(drag.clipId, drag.originalStart, end)
        onScrub(end)
        return
      }
      const len = drag.originalEnd - drag.originalStart
      const start = Math.max(0, Math.min(duration - len, drag.originalStart + deltaSec))
      onClipBounds(drag.clipId, start, start + len)
      onScrub(start)
    }

    const onUp = () => {
      if (drag.kind === 'playhead' && drag.resumeAfter) onResumePlayback()
      setDrag(null)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [drag, duration, secondsPerPixel, onClipBounds, onScrub, onResumePlayback])

  /** Keyboard equivalent of dragging: move one edge, or the whole clip. */
  const nudge = (e: React.KeyboardEvent, placed: PlacedClip, edge: 'start' | 'end' | 'range') => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    if (!editable) return
    e.preventDefault()
    e.stopPropagation()
    const step = (e.shiftKey ? NUDGE_COARSE_S : NUDGE_FINE_S) * (e.key === 'ArrowLeft' ? -1 : 1)
    const id = placed.clip.id
    if (edge === 'start') {
      const start = Math.max(0, Math.min(placed.end - MIN_CLIP_S, placed.start + step))
      onClipBounds(id, start, placed.end)
      onScrub(start)
      return
    }
    if (edge === 'end') {
      const end = Math.max(placed.start + MIN_CLIP_S, Math.min(duration, placed.end + step))
      onClipBounds(id, placed.start, end)
      onScrub(end)
      return
    }
    const len = placed.end - placed.start
    const start = Math.max(0, Math.min(duration - len, placed.start + step))
    onClipBounds(id, start, start + len)
    onScrub(start)
  }

  // --- Click to seek / place a marker --------------------------------------
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false
      return
    }
    const el = timelineRef.current
    if (!el || duration <= 0) return
    const rect = el.getBoundingClientRect()
    onPickTime(timeAt((e.clientX - rect.left) / rect.width))
  }

  // --- Minimap (only useful once zoomed) -----------------------------------
  const minimapRef = useRef<HTMLButtonElement>(null)
  const [panningMap, setPanningMap] = useState(false)
  const panFromEvent = useCallback(
    (clientX: number) => {
      const el = minimapRef.current
      if (!el || duration <= 0) return
      const rect = el.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      centerOn(ratio * duration)
    },
    [centerOn, duration],
  )

  useEffect(() => {
    if (!panningMap) return
    const onMove = (e: MouseEvent) => {
      panFromEvent(e.clientX)
    }
    const onUp = () => {
      setPanningMap(false)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [panningMap, panFromEvent])

  const ticks = useMemo(() => {
    if (duration <= 0) return ['0:00', '0:00', '0:00', '0:00', '0:00']
    const span = view.span || duration
    return [0, 0.25, 0.5, 0.75, 1].map((r) => fmtPrecise(view.start + span * r, span))
  }, [duration, view])

  const hint = armed ? (
    armed.phase === 'start' ? (
      <span>
        Clique na linha do tempo para marcar o <b>início</b> do clipe.
      </span>
    ) : (
      <span>
        Início em <b>{fmt(armed.start)}</b> — agora clique para marcar o <b>fim</b>.
      </span>
    )
  ) : editable ? (
    <span>
      Clique em &quot;Novo clipe&quot; para marcar um corte · role o mouse sobre a linha para dar
      zoom no ponto exato
    </span>
  ) : (
    <span>
      Mostrando <b>só os cortes</b>, na ordem da lista. Para ajustar limites, volte a
      &quot;Vídeos completos&quot;.
    </span>
  )

  return (
    <div className={styles.timelineBlock}>
      <div className={styles.tlTop}>
        <div className={cn(styles.tlHint, armed && styles.tlHintArmed)}>
          {armed ? <Crosshair aria-hidden /> : <Info aria-hidden />}
          {hint}
        </div>
        {zoomed ? (
          <button type="button" className={styles.tlZoomTag} onClick={reset} title="Voltar ao vídeo inteiro">
            <Search aria-hidden />
            <span>{scale >= 10 ? scale.toFixed(0) : scale.toFixed(1)}×</span>
            <X aria-hidden />
          </button>
        ) : null}
      </div>

      <div
        ref={timelineRef}
        className={styles.timeline}
        onClick={handleClick}
        onKeyDown={(e) => {
          // Keyboard scrubbing, and Enter to drop the armed marker exactly
          // where the player is — the most precise way to mark a cut.
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault()
            const step =
              (e.shiftKey ? NUDGE_COARSE_S : NUDGE_FINE_S) * (e.key === 'ArrowLeft' ? -1 : 1)
            onScrub(Math.max(0, Math.min(duration, playhead + step)))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            onPickTime(playhead)
          }
        }}
        role="slider"
        aria-label="Linha do tempo do vídeo"
        aria-valuemin={0}
        aria-valuemax={duration}
        aria-valuenow={playhead}
        tabIndex={0}
      >
        <div className={styles.tlStrip} aria-hidden>
          {Array.from({ length: 24 }).map((_, i) => (
            <div key={i} />
          ))}
        </div>

        {joins.map((at) => (
          // Where one source video ends and the next begins. Purely
          // informative — playback runs straight through it.
          <div
            key={at}
            className={styles.tlJoin}
            style={{ left: `${String(percentOf(at))}%` }}
            aria-hidden
          />
        ))}

        {clips.map((placed) => {
          const { clip, index } = placed
          const color = clipColor(clip.id)
          const left = percentOf(placed.start)
          const width = percentOf(placed.end) - left
          const span = placed.end - placed.start
          return (
            <div
              key={clip.id}
              className={cn(
                styles.tlClip,
                !editable && styles.tlClipStatic,
                clip.hidden && styles.tlClipHidden,
                drag?.kind === 'range' && drag.clipId === clip.id && styles.tlClipDragging,
              )}
              style={{
                left: `${String(left)}%`,
                width: `${String(width)}%`,
                ['--clip-b' as string]: color.border,
                ['--clip-c' as string]: color.fill,
              }}
              onMouseDown={(e) => {
                startClipDrag(e, 'range', placed)
              }}
              onKeyDown={(e) => {
                nudge(e, placed, 'range')
              }}
              role="button"
              tabIndex={0}
              aria-label={
                editable
                  ? `${clipLabel(clip, index)} — mover o clipe inteiro`
                  : clipLabel(clip, index)
              }
              title={
                editable
                  ? `${clipLabel(clip, index)} — arraste para mover · ←/→ ajusta`
                  : `${clipLabel(clip, index)} — volte a "Vídeos completos" para ajustar`
              }
            >
              {/* Positioned against the bar, not the timeline, so a zoom
                  that moves the bar moves the hatch with it. */}
              {span > 0
                ? placed.freezes.map((f) => (
                    <span
                      key={f.id}
                      className={styles.tlFreeze}
                      style={{
                        left: `${String(((f.start - placed.start) / span) * 100)}%`,
                        width: `${String(((f.end - f.start) / span) * 100)}%`,
                      }}
                      aria-hidden
                    />
                  ))
                : null}
              <span className={styles.clipNum}>{index + 1}</span>
              {clip.speed !== 1 ? (
                <span className={styles.clipSpeed}>{formatSpeed(clip.speed)}</span>
              ) : null}
            </div>
          )
        })}

        {editable
          ? clips.flatMap((placed) =>
              (['start', 'end'] as const).map((edge) => {
                const t = edge === 'start' ? placed.start : placed.end
                return (
                  <div
                    key={`${String(placed.clip.id)}-${edge}`}
                    className={cn(
                      styles.tlMarker,
                      drag?.kind === edge && drag.clipId === placed.clip.id && styles.tlMarkerDragging,
                    )}
                    style={{
                      left: `${String(percentOf(t))}%`,
                      ['--mk-c' as string]: clipColor(placed.clip.id).border,
                    }}
                    onMouseDown={(e) => {
                      startClipDrag(e, edge, placed)
                    }}
                    onKeyDown={(e) => {
                      nudge(e, placed, edge)
                    }}
                    role="slider"
                    tabIndex={0}
                    aria-label={`${edge === 'start' ? 'Início' : 'Fim'} do clipe ${String(placed.index + 1)}`}
                    aria-valuemin={0}
                    aria-valuemax={duration}
                    aria-valuenow={t}
                    title={`${edge === 'start' ? 'Início' : 'Fim'} — arraste ou use ←/→ (Shift = 1s)`}
                  />
                )
              }),
            )
          : null}

        {armed?.phase === 'end' ? (
          <div
            className={cn(styles.tlMarker, styles.tlMarkerProvisional)}
            style={{ left: `${String(percentOf(armed.start))}%` }}
            aria-hidden
          />
        ) : null}

        <div
          className={cn(styles.tlPlayhead, drag?.kind === 'playhead' && styles.tlPlayheadDragging)}
          style={{ left: `${String(percentOf(playhead))}%` }}
          onMouseDown={startPlayheadDrag}
          role="slider"
          aria-label="Cursor de reprodução — arraste para navegar pelo vídeo"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={playhead}
          tabIndex={0}
        />
      </div>

      <TagLanes
        tags={tags}
        duration={duration}
        view={{ start: view.start, span: view.span || duration }}
        selectedId={selectedTagId}
        onSelect={onSelectTag}
        onChange={onTagChange}
        onDuplicate={onDuplicateTag}
        onDelete={onDeleteTag}
        ariaLabel="Textos e efeitos na linha do tempo"
      />

      <div className={styles.tlScale} aria-hidden>
        {ticks.map((t, i) => (
          <span key={i}>{t}</span>
        ))}
      </div>

      {zoomed ? (
        <button
          type="button"
          ref={minimapRef}
          className={styles.minimap}
          onMouseDown={(e) => {
            e.preventDefault()
            setPanningMap(true)
            panFromEvent(e.clientX)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') panBy(-PAN_STEP)
            else if (e.key === 'ArrowRight') panBy(PAN_STEP)
            else return
            e.preventDefault()
          }}
          aria-label="Percorrer o vídeo enquanto ampliado"
          title="Arraste (ou use ←/→) para percorrer o vídeo"
        >
          {clips.map((placed) => (
            <div
              key={placed.clip.id}
              className={styles.minimapClip}
              style={{
                left: `${String((placed.start / duration) * 100)}%`,
                width: `${String(((placed.end - placed.start) / duration) * 100)}%`,
                ['--clip-b' as string]: clipColor(placed.clip.id).border,
              }}
            />
          ))}
          <div
            className={styles.minimapPlayhead}
            style={{ left: `${String((playhead / duration) * 100)}%` }}
          />
          <div
            className={styles.minimapWindow}
            style={{
              left: `${String((view.start / duration) * 100)}%`,
              width: `${String(((view.span || duration) / duration) * 100)}%`,
            }}
          />
        </button>
      ) : null}
    </div>
  )
}
